import type { Session } from "@/lib/session";
import { cacheScope, subscribeCacheScope } from "@/lib/ui-cache";
import { fetchWithAuth, type AccessTokenGetter } from "@/lib/utils";

const TTL = 15_000;
const MAX_ENTRIES = 50;
type Detail = { session: Session; fetchedAt: number };
const details = new Map<string, Detail>();
const inFlight = new Map<string, Promise<Session | Response>>();
let currentScope = cacheScope();
let scopeGeneration = 0;

function checkScope(): void {
  const scope = cacheScope();
  if (scope !== currentScope) {
    currentScope = scope;
    scopeGeneration++;
    details.clear();
    inFlight.clear();
  }
}
subscribeCacheScope(checkScope);

function abortError(): DOMException { return new DOMException("Aborted", "AbortError"); }
function awaitCaller<T>(promise: Promise<T>, signal?: AbortSignal | null): Promise<T> {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(abortError());
  return new Promise((resolve, reject) => {
    const abort = () => { signal.removeEventListener("abort", abort); reject(abortError()); };
    signal.addEventListener("abort", abort, { once: true });
    void promise.then(
      (value) => { signal.removeEventListener("abort", abort); resolve(value); },
      (error) => { signal.removeEventListener("abort", abort); reject(error); },
    );
  });
}

/** A fresh Response per caller keeps concurrent JSON consumers independent. */
export async function fetchSessionDetailCached(url: string, init: RequestInit, getToken: AccessTokenGetter): Promise<Response> {
  checkScope();
  const scope = cacheScope();
  const path = new URL(url, "http://local.invalid").pathname;
  const match = /^\/api\/sessions\/([^/]+)$/.exec(path);
  if (!match || (init.method && init.method.toUpperCase() !== "GET")) return fetchWithAuth(url, init, getToken);
  if (init.signal?.aborted) throw abortError();
  const key = `${scope ?? "null"}:${decodeURIComponent(match[1])}`;
  const hit = details.get(key);
  if (hit && Date.now() - hit.fetchedAt < TTL) {
    details.delete(key);
    details.set(key, hit);
    return new Response(JSON.stringify(hit.session), { status: 200, headers: { "content-type": "application/json" } });
  }
  if (hit) details.delete(key);
  let request = inFlight.get(key);
  if (!request) {
    const generation = scopeGeneration;
    // The shared request is not owned by any one caller's abort controller.
    const { signal: _signal, ...sharedInit } = init;
    request = (async () => {
      const response = await fetchWithAuth(url, sharedInit, getToken);
      if (!response.ok) return response;
      const session = await response.json() as Session;
      if (generation === scopeGeneration && scope === cacheScope()) {
        details.delete(key);
        details.set(key, { session, fetchedAt: Date.now() });
        if (details.size > MAX_ENTRIES) details.delete(details.keys().next().value!);
      }
      return session;
    })();
    inFlight.set(key, request);
    void request.finally(() => { if (inFlight.get(key) === request) inFlight.delete(key); }).catch(() => {});
  }
  const result = await awaitCaller(request, init.signal);
  return result instanceof Response ? result.clone()
    : new Response(JSON.stringify(result), { status: 200, headers: { "content-type": "application/json" } });
}
